import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Pressable, ScrollView, StyleSheet, View } from 'react-native';
import { ArrowLeft, Minus, Plus, RotateCcw } from 'lucide-react-native';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { SafeAreaView } from 'react-native-safe-area-context';
import { OriginalDiagramViewer } from './OriginalDiagramViewer';
import { RedrawnDiagramCanvas } from './RedrawnDiagramCanvas';
import { AppButton } from '../ui/AppButton';
import { AppSegmentedControl } from '../ui/AppSegmentedControl';
import { AppText } from '../ui/AppText';
import { ScreenBackground } from '../ui/ScreenBackground';
import { tokens } from '../../theme/tokens';
import { DIAGRAM_API_CONFIG, DIAGRAM_API_MODE } from '../../config/diagramApiConfig';
import { ApiError } from '../../api/client';
import { createDiagramSpecApi, type DiagramSpecRecordDto } from '../../api/diagramSpec';
import { loadDiagramSession, saveDiagramSession } from '../../services/diagramSession';
import {
  buildDiagramImageUrl,
  type DiagramRenderState,
} from '../../utils/diagramApiSupport';
import type {
  ConstraintDragState,
  DiagramSemanticProfile,
  DiagramTrustState,
  DiagramType,
} from '../../types/diagramSpec';
import {
  createDiagramSpecForContext,
  diagramTrustStateCopy,
  diagramTypeLabels,
  originalDiagramFixture,
  resetDiagramPoint,
  resolveDiagramSpecContext,
} from '../../utils/diagramSpecMock';
import {
  parseDiagramSemanticProfile,
  parseDiagramType,
} from '../../utils/photoSearchDiagramContext';

type DiagramView = 'original' | 'redrawn';

const diagramTypes: DiagramType[] = ['function', 'conic', 'triangle', 'circle'];

type DiagramRemoteStatus = 'fixture' | 'loading' | 'ready' | 'missing_source' | 'error';
type DiagramRenderStatus = 'idle' | 'loading' | 'succeeded' | 'failed';

const createClientId = (prefix: string): string =>
  `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const getRemoteTrustState = (record: DiagramSpecRecordDto): DiagramTrustState => {
  if (record.status === 'needs_confirmation' || record.status === 'unavailable') {
    return record.status;
  }
  if (record.status === 'confirmed') {
    return 'redrawn';
  }
  return record.current_revision?.spec.views.redrawn ? 'redrawn' : 'original';
};

export function DiagramQuestionScreen() {
  const router = useRouter();
  const params = useLocalSearchParams<{
    diagramId?: string;
    imageRef?: string;
    textRef?: string;
    diagramType?: string;
    semanticProfile?: string;
    selectedCropImageRef?: string;
    selectedProblemId?: string;
    selectedSegmentId?: string;
    questionNumber?: string;
  }>();
  const routeDiagramType = parseDiagramType(params.diagramType);
  const routeSemanticProfile = parseDiagramSemanticProfile(params.semanticProfile);
  const [diagramType, setDiagramType] = useState<DiagramType>(routeDiagramType ?? 'function');
  const [semanticProfile, setSemanticProfile] = useState<DiagramSemanticProfile>(
    routeSemanticProfile ?? 'default'
  );
  const [trustState, setTrustState] = useState<DiagramTrustState>('redrawn');
  const [view, setView] = useState<DiagramView>('redrawn');
  const [parameter, setParameter] = useState(0.3);
  const [dragState, setDragState] = useState<ConstraintDragState>('idle');
  const [zoom, setZoom] = useState(1);
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
  const [showAuxiliaryLayer, setShowAuxiliaryLayer] = useState(true);
  const [constraintAvailable, setConstraintAvailable] = useState(true);
  const [remoteStatus, setRemoteStatus] = useState<DiagramRemoteStatus>(
    DIAGRAM_API_MODE === 'fixture' ? 'fixture' : 'loading'
  );
  const [remoteMessage, setRemoteMessage] = useState('');
  const [remoteRecord, setRemoteRecord] = useState<DiagramSpecRecordDto | null>(null);
  const [renderStatus, setRenderStatus] = useState<DiagramRenderStatus>('idle');
  const [renderMessage, setRenderMessage] = useState('');
  const [renderResult, setRenderResult] = useState<DiagramRenderState['result']>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const streamAbortRef = useRef<AbortController | null>(null);
  const routeDiagramId = typeof params.diagramId === 'string' ? params.diagramId : null;
  const routeImageRef = typeof params.imageRef === 'string' ? params.imageRef : null;
  const routeSelectedCropImageRef =
    typeof params.selectedCropImageRef === 'string' ? params.selectedCropImageRef : null;
  const routeTextRef = typeof params.textRef === 'string' ? params.textRef : undefined;
  const routeQuestionNumber = typeof params.questionNumber === 'string' ? params.questionNumber : null;
  const fixtureSpec = useMemo(
    () => createDiagramSpecForContext(diagramType, semanticProfile),
    [diagramType, semanticProfile]
  );
  const spec = remoteRecord?.current_revision?.spec ?? fixtureSpec;
  const diagramSourceImageRef = remoteRecord?.source.image_ref ?? routeImageRef;
  const originalAssetRef =
    diagramSourceImageRef ??
    (DIAGRAM_API_MODE === 'fixture' ? originalDiagramFixture.assetRef : 'unavailable');
  const originalFixtureId =
    remoteRecord?.diagram_id ??
    (DIAGRAM_API_MODE === 'fixture' ? originalDiagramFixture.fixtureId : 'staging-upload-source');
  const originalImageUri =
    DIAGRAM_API_MODE === 'staging' && DIAGRAM_API_CONFIG.baseUrl && diagramSourceImageRef
      ? buildDiagramImageUrl(DIAGRAM_API_CONFIG.baseUrl, diagramSourceImageRef)
      : null;
  const canUseRedrawn = trustState === 'redrawn';
  const canPreviewRedrawn = canUseRedrawn || trustState === 'generated_confirmation';
  const canDrag = canUseRedrawn && constraintAvailable && Boolean(spec.interactions[0]?.constraintRef);

  const applyRemoteRecord = useCallback(async (record: DiagramSpecRecordDto) => {
    setRemoteRecord(record);
    if (record.current_revision) {
      const recordContext = resolveDiagramSpecContext(record.current_revision.spec);
      if (recordContext) {
        setDiagramType(recordContext.diagramType);
        setSemanticProfile(recordContext.semanticProfile);
      }
    }
    setRemoteStatus('ready');
    setRemoteMessage('');
    setTrustState(getRemoteTrustState(record));
    if (record.status !== 'confirmed') {
      setView('original');
    }
    await saveDiagramSession({
      diagramId: record.diagram_id,
      revisionId: record.current_revision_id,
      imageRef: record.source.image_ref,
      updatedAt: record.updated_at,
    });
  }, []);

  const handleRemoteError = useCallback((error: unknown) => {
    const code = error instanceof ApiError ? String(error.code) : '';
    const message = error instanceof Error ? error.message : '图形数据请求失败，请稍后重试';
    setRemoteStatus('error');
    setRemoteMessage(message);
    setTrustState('original');
    setView('original');
    if (code === 'diagram_spec_not_confirmed') {
      setTrustState('needs_confirmation');
      setView('original');
    }
    if (code === 'invalid_constraint') {
      setConstraintAvailable(false);
      setDragState('invalid');
    }
  }, []);

  useEffect(() => {
    if (DIAGRAM_API_MODE === 'fixture') {
      return;
    }

    let active = true;
    const hydrateRemoteDiagram = async () => {
      try {
        const api = createDiagramSpecApi();
        const savedSession = routeDiagramId || routeImageRef ? null : await loadDiagramSession();
        const diagramId = routeDiagramId ?? savedSession?.diagramId ?? null;
        if (diagramId) {
          const record = await api.getDiagram(diagramId);
          if (active) {
            await applyRemoteRecord(record);
          }
          return;
        }
        if (routeImageRef) {
          if (!routeDiagramType || !routeSemanticProfile) {
            if (active) {
              setRemoteStatus('missing_source');
              setRemoteMessage('选中题目缺少已确认的图形语义，已停止创建 DiagramSpec');
              setTrustState('original');
              setView('original');
            }
            return;
          }
          const record = await api.createDiagram(
            { type: 'photo', image_ref: routeImageRef, ...(routeTextRef ? { text_ref: routeTextRef } : {}) },
            createDiagramSpecForContext(routeDiagramType, routeSemanticProfile),
            createClientId('diagram-create')
          );
          if (active) {
            await applyRemoteRecord(record);
          }
          return;
        }
        if (active) {
          setRemoteStatus('missing_source');
          setRemoteMessage('需要来自拍照搜题的 imageRef 或已有 diagramId 才能进入真实图形链路');
          setTrustState('original');
          setView('original');
        }
      } catch (error) {
        if (active) {
          handleRemoteError(error);
        }
      }
    };

    void hydrateRemoteDiagram();
    return () => {
      active = false;
    };
  }, [
    applyRemoteRecord,
    handleRemoteError,
    reloadKey,
    routeDiagramId,
    routeDiagramType,
    routeImageRef,
    routeSemanticProfile,
    routeTextRef,
  ]);

  useEffect(() => () => streamAbortRef.current?.abort(), []);

  const resetPoint = useCallback(() => {
    const reset = resetDiagramPoint();
    setDragState(reset.dragState);
    setParameter(reset.parameter);
    setTimeout(() => setDragState('idle'), 180);
  }, []);

  const resetViewport = useCallback(() => {
    setZoom(1);
    setPanOffset({ x: 0, y: 0 });
  }, []);

  const beginExplanation = useCallback(async () => {
    if (DIAGRAM_API_MODE === 'staging') {
      if (!remoteRecord) {
        setRemoteStatus('missing_source');
        setRemoteMessage('真实图形数据尚未加载，不能提交渲染任务');
        return;
      }
      streamAbortRef.current?.abort();
      const controller = new AbortController();
      streamAbortRef.current = controller;
      setRenderStatus('loading');
      setRenderMessage('正在提交真实渲染任务');
      setRenderResult(null);
      setDragState('computing');
      try {
        const api = createDiagramSpecApi();
        const accepted = await api.submitRenderJob(
          {
            revision_id: remoteRecord.current_revision_id,
            render_profile: 'student_canvas_v1',
            trace_id: createClientId('diagram-trace'),
            run_id: createClientId('diagram-run'),
            node_id: 'diagram_renderer',
            attempt: 1,
          },
          createClientId('diagram-render')
        );
        const renderState = await api.consumeRenderEvents(
          accepted.render_job_id,
          accepted.stream_url,
          (_event, nextState) => {
            setRenderMessage(nextState.stage || nextState.status);
            setRenderResult(nextState.result);
          },
          controller.signal
        );
        if (renderState.status === 'succeeded' && renderState.result) {
          setRenderStatus('succeeded');
          setRenderResult(renderState.result);
          setTrustState('redrawn');
          setRenderMessage('真实渲染完成，结果已进入当前图形视图');
        } else if (renderState.status === 'failed') {
          setRenderStatus('failed');
          setRenderMessage(renderState.errorCode || '图形渲染失败');
        } else {
          setRenderStatus('failed');
          setRenderMessage('图形渲染未返回可用结果');
        }
      } catch (error) {
        if (!controller.signal.aborted) {
          setRenderStatus('failed');
          setRenderMessage(error instanceof Error ? error.message : '图形渲染失败');
        }
      } finally {
        if (!controller.signal.aborted) {
          setDragState('idle');
        }
      }
      return;
    }
    setDragState('computing');
    setTimeout(() => setDragState('idle'), 350);
  }, [handleRemoteError, remoteRecord]);

  const invalidateConstraint = useCallback(() => {
    setConstraintAvailable(false);
    setDragState('invalid');
  }, []);

  const restoreConstraint = useCallback(() => {
    setConstraintAvailable(true);
    setDragState('reset');
    setTimeout(() => setDragState('idle'), 180);
  }, []);

  const changeTrustState = useCallback((next: DiagramTrustState) => {
    setTrustState(next);
    if (next === 'generated_confirmation') {
      setView('redrawn');
      setDragState('disabled');
    } else if (next !== 'redrawn') {
      setView('original');
      setDragState(next === 'unavailable' ? 'disabled' : 'idle');
    } else {
      setView('redrawn');
      setConstraintAvailable(true);
      setDragState('idle');
    }
  }, []);

  const confirmRelationship = useCallback(() => changeTrustState('redrawn'), [changeTrustState]);

  const renderStatePanel = () => {
    if (trustState === 'needs_confirmation') {
      return (
        <View style={[styles.statePanel, styles.warningPanel]}>
          <AppText variant="body" weight="bold">表达式中的常数项可能为 3 或 5</AppText>
          <AppText variant="body" tone="muted">请核对原图高亮区域；确认前不会生成交互重绘或进入讲解。</AppText>
          <View style={styles.panelActions}>
            <AppButton label="修正识别结果" variant="secondary" onPress={() => changeTrustState('unavailable')} style={styles.panelButton} />
            <AppButton label="确认后继续" onPress={confirmRelationship} style={styles.panelButton} />
          </View>
        </View>
      );
    }

    if (trustState === 'unavailable') {
      return (
        <View style={[styles.statePanel, styles.dangerPanel]}>
          <AppText variant="body" weight="bold" tone="danger">缺少可确认的定义域或曲线表达式</AppText>
          <AppText variant="body" tone="muted">已保留原题图。你可以重新拍摄，或基于已确认文字继续。</AppText>
          <View style={styles.panelActions}>
            <AppButton label="基于已确认文字继续" tone="success" onPress={() => changeTrustState('generated_confirmation')} style={styles.panelButton} />
            <AppButton label="重新拍摄" variant="secondary" onPress={() => router.push('/photo-search')} style={styles.panelButton} />
          </View>
        </View>
      );
    }

    if (trustState === 'generated_confirmation') {
      return (
        <View style={[styles.statePanel, styles.generatedPanel]}>
          <AppText variant="body" weight="bold">示意图不代表边长、角度或比例</AppText>
          <AppText variant="body" tone="muted">仅使用题干已确认关系：点 D 是 BC 中点，连接 AD；未添加 AD 垂直 BC。</AppText>
          <View style={styles.panelActions}>
            <AppButton label="返回补充题干" variant="secondary" onPress={() => changeTrustState('original')} style={styles.panelButton} />
            <AppButton label="确认示意图" onPress={confirmRelationship} style={styles.panelButton} />
          </View>
        </View>
      );
    }

    return null;
  };

  return (
    <SafeAreaView style={styles.screen}>
      <ScreenBackground style={styles.background}>
        <View style={styles.header}>
          <AppButton
            label="返回"
            variant="ghost"
            onPress={() => router.back()}
            leftSlot={<ArrowLeft size={18} color={tokens.colors.primary} />}
            style={styles.backButton}
          />
          <View style={styles.headerCopy}>
            <AppText variant="title" weight="bold">{diagramTrustStateCopy[trustState].title}</AppText>
            <AppText variant="caption" tone="muted">{diagramTrustStateCopy[trustState].detail}</AppText>
          </View>
        </View>

        <ScrollView contentContainerStyle={styles.content}>
          <View style={styles.typeTabs}>
            {diagramTypes.map((type) => {
              const selected = diagramType === type;
              return (
                <Pressable
                  key={type}
                  accessibilityRole="button"
                  accessibilityState={{ selected }}
                  onPress={() => {
                    setDiagramType(type);
                    setSemanticProfile('default');
                    resetPoint();
                  }}
                  style={[styles.typeTab, selected ? styles.typeTabActive : null]}
                >
                  <AppText variant="caption" weight="bold" style={selected ? styles.typeTabTextActive : null}>
                    {diagramTypeLabels[type]}
                  </AppText>
                </Pressable>
              );
            })}
          </View>

          <AppSegmentedControl
            options={[
              { id: 'original', label: '原图' },
              { id: 'redrawn', label: '清晰重绘' },
            ]}
            value={view}
            onChange={(next) => setView(next === 'redrawn' && !canUseRedrawn ? 'original' : next)}
          />

          {DIAGRAM_API_MODE === 'staging' && remoteStatus !== 'ready' ? (
            <View style={[styles.statePanel, remoteStatus === 'error' ? styles.dangerPanel : styles.warningPanel]}>
              <AppText variant="body" weight="bold">
                {remoteStatus === 'loading' ? '正在同步图形数据' : '真实图形链路尚未就绪'}
              </AppText>
              <AppText variant="body" tone="muted">{remoteMessage}</AppText>
              <AppButton label="重新连接" variant="secondary" onPress={() => setReloadKey((value) => value + 1)} />
            </View>
          ) : null}

          {DIAGRAM_API_MODE === 'staging' && renderStatus !== 'idle' ? (
            <View
              accessibilityLabel={
                renderResult
                  ? `真实渲染结果 ${renderResult.artifactRef}`
                  : '真实渲染状态'
              }
              style={[
                styles.statePanel,
                renderStatus === 'failed' ? styles.dangerPanel : styles.generatedPanel,
              ]}
            >
              <AppText variant="body" weight="bold">
                {renderStatus === 'loading'
                  ? '正在生成真实渲染'
                  : renderStatus === 'succeeded'
                    ? '真实渲染结果'
                    : '真实渲染失败'}
              </AppText>
              <AppText variant="body" tone="muted">{renderMessage}</AppText>
              {renderStatus === 'succeeded' && renderResult ? (
                <>
                  <AppText variant="body">后端渲染结果已用于当前清晰重绘视图。</AppText>
                  <AppText variant="caption" tone="muted" numberOfLines={1}>
                    artifact_ref: {renderResult.artifactRef}
                  </AppText>
                </>
              ) : null}
            </View>
          ) : null}

          {view === 'original' || !canPreviewRedrawn ? (
            <OriginalDiagramViewer
              statement={
                routeQuestionNumber && routeSelectedCropImageRef
                  ? `已选择第 ${routeQuestionNumber} 题；原图与选题裁剪是制图事实依据。`
                  : `${diagramTypeLabels[diagramType]}原图是制图事实依据。`
              }
              assetRef={originalAssetRef}
              fixtureId={originalFixtureId}
              imageUri={originalImageUri}
            />
          ) : (
            <>
              <View style={styles.canvasTools}>
                <AppButton label="缩小" variant="secondary" onPress={() => setZoom((current) => Math.max(0.85, Number((current - 0.1).toFixed(2))))} leftSlot={<Minus size={16} color={tokens.colors.primary} />} style={styles.toolButton} />
                <AppText variant="caption" tone="muted" weight="bold" style={styles.zoomLabel}>{Math.round(zoom * 100)}%</AppText>
                <AppButton label="放大" variant="secondary" onPress={() => setZoom((current) => Math.min(1.25, Number((current + 0.1).toFixed(2))))} leftSlot={<Plus size={16} color={tokens.colors.primary} />} style={styles.toolButton} />
                <AppButton label="复位视图" variant="ghost" onPress={resetViewport} leftSlot={<RotateCcw size={16} color={tokens.colors.primary} />} style={styles.toolButton} />
                <Pressable accessibilityRole="button" accessibilityState={{ selected: showAuxiliaryLayer }} onPress={() => setShowAuxiliaryLayer((current) => !current)} style={[styles.layerButton, showAuxiliaryLayer ? styles.layerButtonActive : null]}>
                  <AppText variant="caption" weight="bold" style={showAuxiliaryLayer ? styles.layerTextActive : null}>辅助线</AppText>
                </Pressable>
              </View>
              <RedrawnDiagramCanvas
                diagramType={diagramType}
                semanticProfile={semanticProfile}
                parameter={parameter}
                dragState={dragState}
                interactive={canDrag}
                zoom={zoom}
                panOffset={panOffset}
                showAuxiliaryLayer={showAuxiliaryLayer}
                onParameterChange={(nextParameter, nextState) => {
                  setParameter(nextParameter);
                  setDragState(nextState);
                }}
                onDragStateChange={setDragState}
                onPanOffsetChange={setPanOffset}
              />
            </>
          )}

          {view === 'redrawn' && canPreviewRedrawn ? (
            <View style={styles.summaryCard}>
              <AppText variant="body" weight="bold">图形摘要</AppText>
              <AppText variant="body" tone="muted">{spec.summary}</AppText>
              <AppText variant="caption" tone="muted">动点状态：{dragState}</AppText>
            </View>
          ) : null}

          {renderStatePanel()}

          {canUseRedrawn ? (
            <View style={styles.actions}>
              <AppButton
                label="复位动点"
                variant="secondary"
                onPress={resetPoint}
                leftSlot={<RotateCcw size={17} color={tokens.colors.primary} />}
                style={styles.actionButton}
              />
              <AppButton label="开始 AI 讲解" onPress={() => void beginExplanation()} style={styles.actionButton} />
            </View>
          ) : null}

          {trustState === 'redrawn' ? (
            <AppButton
              label={constraintAvailable ? '约束异常，静态查看' : '恢复受约束拖动'}
              variant="secondary"
              onPress={constraintAvailable ? invalidateConstraint : restoreConstraint}
              style={styles.constraintButton}
            />
          ) : null}

          {trustState === 'redrawn' ? (
            <AppButton label="与原图不一致？" variant="ghost" onPress={() => changeTrustState('needs_confirmation')} style={styles.reportButton} />
          ) : null}
        </ScrollView>
      </ScreenBackground>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  screen: { flex: 1, backgroundColor: tokens.colors.background },
  background: { flex: 1 },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.md,
    paddingHorizontal: tokens.layout.screenPadding,
    paddingTop: tokens.spacing.md,
    paddingBottom: tokens.spacing.sm,
  },
  backButton: { minHeight: 40, paddingHorizontal: tokens.spacing.sm },
  headerCopy: { flex: 1 },
  content: {
    gap: tokens.spacing.lg,
    paddingHorizontal: tokens.layout.screenPadding,
    paddingTop: tokens.spacing.md,
    paddingBottom: tokens.spacing.xxl,
  },
  typeTabs: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: tokens.spacing.sm,
  },
  typeTab: {
    minHeight: 36,
    borderRadius: tokens.radius.full,
    borderWidth: tokens.border.width,
    borderColor: tokens.colors.border,
    paddingHorizontal: tokens.spacing.md,
    justifyContent: 'center',
    backgroundColor: tokens.colors.surface,
  },
  typeTabActive: { backgroundColor: tokens.colors.dusk, borderColor: tokens.colors.dusk },
  typeTabTextActive: { color: tokens.colors.white },
  canvasTools: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: tokens.spacing.sm,
  },
  toolButton: {
    minHeight: 38,
    paddingHorizontal: tokens.spacing.sm,
  },
  zoomLabel: {
    minWidth: 42,
    textAlign: 'center',
  },
  layerButton: {
    minHeight: 38,
    borderRadius: tokens.radius.full,
    borderWidth: tokens.border.width,
    borderColor: tokens.colors.border,
    paddingHorizontal: tokens.spacing.md,
    justifyContent: 'center',
    backgroundColor: tokens.colors.surface,
  },
  layerButtonActive: {
    borderColor: tokens.colors.dusk,
    backgroundColor: tokens.colors.dusk,
  },
  layerTextActive: { color: tokens.colors.white },
  summaryCard: {
    gap: tokens.spacing.xs,
    borderRadius: tokens.radius.md,
    padding: tokens.spacing.lg,
    backgroundColor: tokens.colors.surface,
    ...tokens.shadow.card,
  },
  statePanel: {
    gap: tokens.spacing.md,
    borderRadius: tokens.radius.md,
    borderWidth: tokens.border.width,
    padding: tokens.spacing.lg,
  },
  warningPanel: { borderColor: tokens.colors.warning, backgroundColor: tokens.colors.warningSoft },
  dangerPanel: { borderColor: tokens.colors.danger, backgroundColor: tokens.colors.dangerSoft },
  generatedPanel: { borderColor: tokens.colors.summit, backgroundColor: tokens.colors.summitSoft },
  panelActions: { flexDirection: 'row', gap: tokens.spacing.sm },
  panelButton: { flex: 1, paddingHorizontal: tokens.spacing.sm },
  actions: { flexDirection: 'row', gap: tokens.spacing.sm },
  actionButton: { flex: 1, paddingHorizontal: tokens.spacing.sm },
  reportButton: { alignSelf: 'flex-start', paddingHorizontal: 0 },
  constraintButton: { alignSelf: 'flex-start' },
});
